import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
  LineChart, Line, CartesianGrid,
} from 'recharts'
import {
  RefreshCw, FileText, GitBranch, Users, Smartphone,
  TrendingUp, TrendingDown, CheckCircle2, Clock, AlertCircle,
} from 'lucide-react'
import { reportsApi } from '@/lib/api'

// ── Colour palette ────────────────────────────────────────────────────────────
const BLUE   = ['#1e40af', '#3b82f6', '#93c5fd', '#bfdbfe', '#dbeafe']
const GREEN  = ['#15803d', '#22c55e', '#86efac', '#bbf7d0']
const STATUS_COLORS: Record<string, string> = {
  Submetido:   '#3b82f6',
  Aprovado:    '#22c55e',
  Rejeitado:   '#ef4444',
  Rascunho:    '#9ca3af',
  Pendente:    '#f59e0b',
  'Em progresso': '#6366f1',
  Concluído:   '#22c55e',
  Cancelado:   '#9ca3af',
}
const PIE_COLORS = ['#1e40af', '#22c55e', '#f59e0b', '#ef4444', '#6366f1', '#ec4899', '#14b8a6']

// ── Helpers ───────────────────────────────────────────────────────────────────
function KpiCard({ label, value, sub, icon: Icon, trend }: {
  label: string; value: number | string; sub?: string; icon?: React.ElementType; trend?: 'up' | 'down' | 'neutral'
}) {
  return (
    <div className="card py-4 px-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-gray-400">{label}</p>
          <p className="mt-1 text-3xl font-bold text-gray-900">{Number(value).toLocaleString('pt-PT')}</p>
          {sub && (
            <p className={`mt-1 flex items-center gap-1 text-xs font-medium ${
              trend === 'up' ? 'text-green-600' : trend === 'down' ? 'text-red-500' : 'text-gray-400'
            }`}>
              {trend === 'up' && <TrendingUp className="h-3 w-3" />}
              {trend === 'down' && <TrendingDown className="h-3 w-3" />}
              {sub}
            </p>
          )}
        </div>
        {Icon && (
          <div className="rounded-xl bg-primary-50 p-3">
            <Icon className="h-6 w-6 text-primary-600" />
          </div>
        )}
      </div>
    </div>
  )
}

function ChartCard({ title, subtitle, children, minH = 260 }: {
  title: string; subtitle?: string; children: React.ReactNode; minH?: number
}) {
  return (
    <div className="card">
      <div className="mb-4">
        <h3 className="text-base font-semibold text-gray-900">{title}</h3>
        {subtitle && <p className="mt-0.5 text-xs text-gray-400">{subtitle}</p>}
      </div>
      <div style={{ minHeight: minH }}>{children}</div>
    </div>
  )
}

function Empty() {
  return (
    <div className="flex h-40 items-center justify-center rounded-lg bg-gray-50">
      <p className="text-sm text-gray-400 italic">Sem dados disponíveis</p>
    </div>
  )
}

function Skeleton() {
  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[1,2,3,4].map(i => (
          <div key={i} className="card py-4 px-5 animate-pulse">
            <div className="h-3 w-24 rounded bg-gray-200 mb-3" />
            <div className="h-8 w-16 rounded bg-gray-200" />
          </div>
        ))}
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        {[1,2,3,4].map(i => (
          <div key={i} className="card animate-pulse" style={{ minHeight: 280 }}>
            <div className="h-4 w-40 rounded bg-gray-200 mb-6" />
            <div className="h-48 rounded bg-gray-100" />
          </div>
        ))}
      </div>
    </div>
  )
}

// Custom tooltip for recharts
const ChartTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-md text-xs">
      {label && <p className="font-semibold text-gray-700 mb-1">{label}</p>}
      {payload.map((p: any, i: number) => (
        <p key={i} style={{ color: p.color ?? '#1e40af' }}>
          {p.name ?? ''} <strong>{p.value?.toLocaleString('pt-PT')}</strong>
        </p>
      ))}
    </div>
  )
}

const fmtMonth = (m: string) => {
  if (!m) return m
  const [y, mo] = m.split('-')
  const names = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez']
  return `${names[parseInt(mo, 10) - 1]} '${y.slice(2)}`
}

// ── Tabs ───────────────────────────────────────────────────────────────────────
const TABS = [
  { key: 'submissions', label: 'Submissões',   Icon: FileText   },
  { key: 'workflows',   label: 'Workflows',    Icon: GitBranch  },
  { key: 'users',       label: 'Utilizadores', Icon: Users      },
  { key: 'devices',     label: 'Dispositivos', Icon: Smartphone },
] as const
type TabKey = typeof TABS[number]['key']

// ── Section: Submissões ───────────────────────────────────────────────────────
function SubmissionsSection({ data }: { data: any }) {
  const thisMonth = data.this_month as number
  const lastMonth = data.last_month as number
  const pct = lastMonth > 0
    ? Math.round(((thisMonth - lastMonth) / lastMonth) * 100)
    : null

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard label="Total de Submissões" value={data.total} icon={FileText} />
        <KpiCard
          label="Este Mês"
          value={thisMonth}
          sub={pct != null ? `${pct > 0 ? '+' : ''}${pct}% vs mês anterior` : undefined}
          trend={pct != null ? (pct >= 0 ? 'up' : 'down') : undefined}
        />
        <KpiCard label="Mês Anterior" value={lastMonth} />
        <KpiCard label="Formulários com Submissões" value={data.by_form?.length ?? 0} />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <ChartCard title="Submissões por Mês" subtitle="Últimos 6 meses">
          {data.by_month?.length ? (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={data.by_month.map((d: any) => ({ ...d, month: fmtMonth(d.month) }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="count" fill={BLUE[0]} radius={[4,4,0,0]} name="Submissões" />
              </BarChart>
            </ResponsiveContainer>
          ) : <Empty />}
        </ChartCard>

        <ChartCard title="Distribuição por Estado">
          {data.by_status?.length ? (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={data.by_status} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={90}
                  label={({ name, percent }) => `${name} ${(percent*100).toFixed(0)}%`} labelLine={false}>
                  {data.by_status.map((entry: any) => (
                    <Cell key={entry.name} fill={STATUS_COLORS[entry.name] ?? PIE_COLORS[0]} />
                  ))}
                </Pie>
                <Tooltip content={<ChartTooltip />} />
                <Legend iconType="circle" iconSize={8} formatter={(v) => <span style={{fontSize:11}}>{v}</span>} />
              </PieChart>
            </ResponsiveContainer>
          ) : <Empty />}
        </ChartCard>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <ChartCard title="Top Formulários" subtitle="Por número de submissões">
          {data.by_form?.length ? (
            <ResponsiveContainer width="100%" height={Math.max(200, data.by_form.length * 36)}>
              <BarChart data={[...data.by_form].reverse()} layout="vertical">
                <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={140} />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="count" fill={BLUE[1]} radius={[0,4,4,0]} name="Submissões" />
              </BarChart>
            </ResponsiveContainer>
          ) : <Empty />}
        </ChartCard>

        <ChartCard title="Top Utilizadores" subtitle="Por número de submissões">
          {data.by_user?.length ? (
            <ResponsiveContainer width="100%" height={Math.max(200, data.by_user.length * 36)}>
              <BarChart data={[...data.by_user].reverse()} layout="vertical">
                <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={130} />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="count" fill={GREEN[0]} radius={[0,4,4,0]} name="Submissões" />
              </BarChart>
            </ResponsiveContainer>
          ) : <Empty />}
        </ChartCard>
      </div>

      {data.by_weekday?.length > 0 && (
        <ChartCard title="Submissões por Dia da Semana" subtitle="Últimos 3 meses">
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={data.by_weekday}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip content={<ChartTooltip />} />
              <Bar dataKey="count" fill={BLUE[2]} radius={[4,4,0,0]} name="Submissões" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      )}
    </div>
  )
}

// ── Section: Workflows ────────────────────────────────────────────────────────
function WorkflowsSection({ data }: { data: any }) {
  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard label="Total de Instâncias" value={data.total} icon={GitBranch} />
        <KpiCard label="Em Curso" value={data.active} icon={Clock} />
        <KpiCard label="Concluídas" value={data.completed} icon={CheckCircle2} />
        <KpiCard label="Tarefas Pendentes" value={data.pending_tasks}
          sub={data.pending_tasks > 0 ? 'Aguardam ação' : 'Tudo em dia'}
          trend={data.pending_tasks > 0 ? 'down' : 'up'} icon={AlertCircle} />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <ChartCard title="Instâncias por Workflow">
          {data.by_workflow?.length ? (
            <ResponsiveContainer width="100%" height={Math.max(200, data.by_workflow.length * 36)}>
              <BarChart data={[...data.by_workflow].reverse()} layout="vertical">
                <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={140} />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="count" fill={BLUE[0]} radius={[0,4,4,0]} name="Instâncias" />
              </BarChart>
            </ResponsiveContainer>
          ) : <Empty />}
        </ChartCard>

        <ChartCard title="Tarefas por Estado">
          {data.tasks_by_status?.length ? (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={data.tasks_by_status} dataKey="count" nameKey="name" cx="50%" cy="50%"
                  outerRadius={90} label={({ name, percent }) => `${name} ${(percent*100).toFixed(0)}%`} labelLine={false}>
                  {data.tasks_by_status.map((entry: any) => (
                    <Cell key={entry.key} fill={entry.color ?? PIE_COLORS[0]} />
                  ))}
                </Pie>
                <Tooltip content={<ChartTooltip />} />
                <Legend iconType="circle" iconSize={8} formatter={(v) => <span style={{fontSize:11}}>{v}</span>} />
              </PieChart>
            </ResponsiveContainer>
          ) : <Empty />}
        </ChartCard>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {data.active_by_state?.length > 0 && (
          <ChartCard title="Instâncias Ativas por Estado Atual">
            <ResponsiveContainer width="100%" height={Math.max(200, data.active_by_state.length * 36)}>
              <BarChart data={[...data.active_by_state].reverse()} layout="vertical">
                <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={140} />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="count" fill={BLUE[1]} radius={[0,4,4,0]} name="Ativas" />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        )}
        {data.by_month?.length > 0 && (
          <ChartCard title="Novas Instâncias por Mês" subtitle="Últimos 6 meses">
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={data.by_month.map((d: any) => ({ ...d, month: fmtMonth(d.month) }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="count" fill={GREEN[0]} radius={[4,4,0,0]} name="Instâncias" />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        )}
      </div>
    </div>
  )
}

// ── Section: Utilizadores ─────────────────────────────────────────────────────
function UsersSection({ data }: { data: any }) {
  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard label="Total de Utilizadores" value={data.total} icon={Users} />
        <KpiCard label="Ativos" value={data.active} />
        <KpiCard label="Inativos" value={data.inactive} />
        <KpiCard label="Logins (30 dias)" value={data.logins_last_30d} sub="Sessões bem-sucedidas" />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <ChartCard title="Utilizadores por Role">
          {data.by_role?.length ? (
            <ResponsiveContainer width="100%" height={Math.max(200, data.by_role.length * 36)}>
              <BarChart data={[...data.by_role].reverse()} layout="vertical">
                <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={130} />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="count" fill={BLUE[0]} radius={[0,4,4,0]} name="Utilizadores" />
              </BarChart>
            </ResponsiveContainer>
          ) : <Empty />}
        </ChartCard>

        <ChartCard title="Estado dos Utilizadores">
          {(data.active > 0 || data.inactive > 0) ? (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie
                  data={[
                    { name: 'Ativos',        count: data.active,        fill: GREEN[0] },
                    { name: 'Inativos',      count: data.inactive,      fill: '#9ca3af' },
                    { name: 'Acesso Mobile', count: data.mobile_access, fill: BLUE[1] },
                  ].filter(d => d.count > 0)}
                  dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={90}
                  label={({ name, percent }) => `${name} ${(percent*100).toFixed(0)}%`} labelLine={false}>
                  {[GREEN[0], '#9ca3af', BLUE[1]].map((c, i) => <Cell key={i} fill={c} />)}
                </Pie>
                <Tooltip content={<ChartTooltip />} />
                <Legend iconType="circle" iconSize={8} formatter={(v) => <span style={{fontSize:11}}>{v}</span>} />
              </PieChart>
            </ResponsiveContainer>
          ) : <Empty />}
        </ChartCard>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {data.by_month?.length > 0 && (
          <ChartCard title="Novos Utilizadores por Mês" subtitle="Últimos 6 meses">
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={data.by_month.map((d: any) => ({ ...d, month: fmtMonth(d.month) }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="count" fill={BLUE[1]} radius={[4,4,0,0]} name="Novos utilizadores" />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        )}
        {data.logins_by_day?.length > 0 && (
          <ChartCard title="Logins por Dia" subtitle="Últimos 30 dias">
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={data.logins_by_day}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="day" tick={{ fontSize: 9 }} tickFormatter={(v) => v?.slice(5) ?? v} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip content={<ChartTooltip />} />
                <Line type="monotone" dataKey="count" stroke={BLUE[0]} strokeWidth={2} dot={false} name="Logins" />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
        )}
      </div>
    </div>
  )
}

// ── Section: Dispositivos ─────────────────────────────────────────────────────
function DevicesSection({ data }: { data: any }) {
  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard label="Total Dispositivos" value={data.total} icon={Smartphone} />
        <KpiCard label="Ativos" value={data.active} />
        <KpiCard label="Inativos" value={Math.max(0, data.total - data.active)} />
        <KpiCard label="Modelos Distintos" value={data.by_model?.length ?? 0} />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <ChartCard title="Sistema Operativo" subtitle="Baseado em submissões identificadas">
          {data.by_os?.length ? (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={data.by_os} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={90}
                  label={({ name, percent }) => `${name} ${(percent*100).toFixed(0)}%`} labelLine={false}>
                  {data.by_os.map((_: any, i: number) => (
                    <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip content={<ChartTooltip />} />
                <Legend iconType="circle" iconSize={8} formatter={(v) => <span style={{fontSize:11}}>{v}</span>} />
              </PieChart>
            </ResponsiveContainer>
          ) : <Empty />}
        </ChartCard>

        <ChartCard title="Top Modelos" subtitle="Por número de submissões">
          {data.by_model?.length ? (
            <ResponsiveContainer width="100%" height={Math.max(200, data.by_model.length * 34)}>
              <BarChart data={[...data.by_model].reverse()} layout="vertical">
                <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={140} />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="count" fill={BLUE[0]} radius={[0,4,4,0]} name="Submissões" />
              </BarChart>
            </ResponsiveContainer>
          ) : <Empty />}
        </ChartCard>
      </div>

      {data.submissions_by_device?.length > 0 && (
        <ChartCard title="Top Dispositivos por Submissões">
          <ResponsiveContainer width="100%" height={Math.max(200, data.submissions_by_device.length * 36)}>
            <BarChart data={[...data.submissions_by_device].reverse()} layout="vertical">
              <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={150} />
              <Tooltip content={<ChartTooltip />} />
              <Bar dataKey="count" fill={GREEN[0]} radius={[0,4,4,0]} name="Submissões" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      )}
    </div>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function ReportsPage() {
  const [tab, setTab] = useState<TabKey>('submissions')
  const qc = useQueryClient()

  const { data: raw, isLoading, isError } = useQuery({
    queryKey: ['reports-overview'],
    queryFn: () => reportsApi.overview(),
    staleTime: 5 * 60 * 1000,
  })

  const report = (raw as any)?.data

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Relatórios</h1>
          <p className="mt-1 text-sm text-gray-500">Dados agregados em tempo real</p>
        </div>
        <button
          onClick={() => qc.invalidateQueries({ queryKey: ['reports-overview'] })}
          className="btn btn-secondary"
        >
          <RefreshCw className="h-4 w-4" />
          <span className="ml-2">Atualizar</span>
        </button>
      </div>

      {/* Tab nav */}
      <div className="mt-6 flex gap-1 rounded-xl bg-gray-100 p-1">
        {TABS.map(({ key, label, Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`flex flex-1 items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-all ${
              tab === key
                ? 'bg-white text-primary-700 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Icon className="h-4 w-4" />
            <span className="hidden sm:inline">{label}</span>
          </button>
        ))}
      </div>

      <div className="mt-6">
        {isLoading && <Skeleton />}

        {isError && (
          <div className="flex flex-col items-center justify-center rounded-xl border border-red-100 bg-red-50 py-16 text-center">
            <AlertCircle className="h-10 w-10 text-red-400 mb-3" />
            <p className="font-semibold text-red-700">Erro ao carregar relatórios</p>
            <p className="mt-1 text-sm text-red-500">Verifique a ligação e tente novamente.</p>
            <button
              onClick={() => qc.invalidateQueries({ queryKey: ['reports-overview'] })}
              className="mt-4 btn btn-secondary text-sm"
            >
              Tentar novamente
            </button>
          </div>
        )}

        {!isLoading && !isError && report && (
          <>
            {tab === 'submissions' && <SubmissionsSection data={report.submissions} />}
            {tab === 'workflows'   && <WorkflowsSection   data={report.workflows}   />}
            {tab === 'users'       && <UsersSection        data={report.users}       />}
            {tab === 'devices'     && <DevicesSection      data={report.devices}     />}
          </>
        )}
      </div>
    </div>
  )
}

